import React, { useState } from "react";
import {
  Button,
  Form,
  message,
  Divider,
  Typography,
  Spin,
  Row,
  Col,
  Popconfirm,
  Drawer,
  Upload,
  Image,
} from "antd";
import {
  DeleteOutlined,
  ReloadOutlined,
  UndoOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { PageHeader } from "../../components";
import {
  drawerTitle,
  drawerFooter,
  DRAWER_WIDE_WIDTH,
} from "../../components/AppUi/appUi";
import { useWebInfoService } from "../../Services";
import {
  SectionCard,
  milestonesSection,
  valuesSection,
  teamSection,
  contactInfoSection,
  faqsSection,
  socialLinksSection,
  businessHoursSection,
  heroSection,
  aboutSection,
  footerSection,
  WebProductsCard,
} from "./common";
import { IMAGE_URL } from "../../Constant";

const { Text } = Typography;

const IMAGE_FIELDS = [
  { key: "web_logo", label: "Website Logo", httpField: "logo" },
  { key: "web_favicon", label: "Website Favicon", httpField: "favicon" },
  { key: "background_image", label: "Background Image", httpField: "background_image" },
];

const sections = [
  milestonesSection,
  valuesSection,
  teamSection,
  heroSection,
  aboutSection,
  contactInfoSection,
  faqsSection,
  socialLinksSection,
  businessHoursSection,
  footerSection,
];

export const WebInfoManagement = () => {
  const {
    webInfoQuery,
    createOrUpdateWebInfoMutation,
    deleteWebInfoMutation,
    uploadWebLogoMutation,
    uploadWebFaviconMutation,
    uploadBackgroundImageMutation,
  } = useWebInfoService();
  const { data: webInfoData, isLoading, refetch } = webInfoQuery;

  const handleImageUpload = async (field: string, file: File) => {
    let res;
    if (field === "background_image") {
      res = await uploadBackgroundImageMutation.mutateAsync(file);
    } else if (field === "favicon") {
      res = await uploadWebFaviconMutation.mutateAsync(file);
    } else {
      res = await uploadWebLogoMutation.mutateAsync(file);
    }
    if (res?.success) {
      message.success("Image uploaded");
      refetch();
    } else {
      message.error(res?.message || "Upload failed");
    }
  };

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [sectionData, setSectionData] = useState<any[]>([]);
  const [webProductsDrawerOpen, setWebProductsDrawerOpen] = useState(false);
  const [form] = Form.useForm();

  const openDrawer = (section: string, data: any[]) => {
    setEditingSection(section);
    setSectionData(JSON.parse(JSON.stringify(data)));
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingSection(null);
    setSectionData([]);
    form.resetFields();
  };

  const activeSection = sections.find((s) => s.meta.key === editingSection);

  const handleSave = async () => {
    if (!editingSection) return;
    try {
      const values = await form.validateFields();
      const res = await createOrUpdateWebInfoMutation.mutateAsync({
        [editingSection]: values[editingSection],
      });
      if (res?.success) {
        message.success(`${editingSection.replace("_", " ")} saved`);
        closeDrawer();
      } else {
        message.error(res?.message || "Failed to save");
      }
    } catch {
      message.error("Please fix form errors");
    }
  };

  const getSectionData = (key: string): any[] => {
    if (!webInfoData) return [];
    return (webInfoData as any)[key] || [];
  };

  const handleDeleteAll = async () => {
    const res = await deleteWebInfoMutation.mutateAsync();
    if (res?.success) {
      message.success("All website content deleted");
    } else {
      message.error(res?.message || "Failed to delete");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spin size="large" tip="Loading website content..." />
      </div>
    );
  }

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <PageHeader
        title="Website Content Management"
        subText="Manage images, milestones, values, team, hero, about, contact, FAQs, social links, business hours, and footer"
        handleButtons={[
          { title: "Refresh", icon: ReloadOutlined, onClick: () => refetch() },
        ]}
      />

      <Row gutter={[16, 16]} className="!mt-4 !mb-4">
        {IMAGE_FIELDS.map(({ key, label, httpField }) => {
          const filename = (webInfoData as any)?.[key];
          return (
            <Col xs={24} md={8} key={key}>
              <div className="bg-white rounded-lg border border-gray-200 p-4 flex flex-col items-center gap-3">
                <h3 className="text-sm font-semibold text-gray-700 m-0">
                  {label}
                </h3>
                {filename ? (
                  <Image
                    src={`${IMAGE_URL}/${filename}`}
                    alt={label}
                    width={100}
                    className="object-contain rounded border border-gray-100"
                    fallback="/images/noimage.png"
                  />
                ) : (
                  <div className="w-[100px] h-[70px] bg-gray-100 rounded flex items-center justify-center text-gray-400 text-xs">
                    No image
                  </div>
                )}
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    handleImageUpload(httpField, file);
                    return false;
                  }}
                >
                  <Button
                    size="small"
                    icon={<UploadOutlined />}
                    loading={
                      uploadWebLogoMutation.isPending ||
                      uploadWebFaviconMutation.isPending ||
                      uploadBackgroundImageMutation.isPending
                    }
                  >
                    Upload
                  </Button>
                </Upload>
              </div>
            </Col>
          );
        })}
      </Row>

      <Row gutter={[16, 16]} className="!mt-4">
        <Col xs={24} md={24}>
          <WebProductsCard
            onManage={() => setWebProductsDrawerOpen(true)}
            webProductsDrawerOpen={webProductsDrawerOpen}
            setWebProductsDrawerOpen={setWebProductsDrawerOpen}
          />
        </Col>
      </Row>

      <Divider className="!my-6" />

      <Row gutter={[16, 16]}>
        {sections.map(({ meta, Preview }) => {
          const data = getSectionData(meta.key);
          return (
            <Col xs={24} md={12} key={meta.key}>
              <SectionCard
                meta={meta}
                data={data}
                onEdit={() => openDrawer(meta.key, data)}
              >
                <Preview data={data} />
              </SectionCard>
            </Col>
          );
        })}
      </Row>

      <div className="flex items-center gap-3">
        <Popconfirm
          title="Delete all website content?"
          description="This action cannot be undone. All sections will be cleared."
          onConfirm={handleDeleteAll}
          okText="Delete All"
          okType="danger"
          cancelText="Cancel"
        >
          <Button danger icon={<DeleteOutlined />} size="small">
            Delete All Content
          </Button>
        </Popconfirm>
        <Text type="secondary" className="text-xs">
          Removes all sections and resets to defaults on the website.
        </Text>
      </div>

      <Drawer
        title={drawerTitle(
          activeSection ? `Edit ${activeSection.meta.label}` : "",
          activeSection
            ? `Manage items for ${activeSection.meta.label.toLowerCase()}`
            : "",
        )}
        open={drawerOpen}
        onClose={closeDrawer}
        placement="right"
        width={DRAWER_WIDE_WIDTH}
        destroyOnClose
        footer={drawerFooter({
          left: activeSection ? (
            <Button
              icon={<UndoOutlined />}
              size="small"
              type="text"
              onClick={() => {
                const defaults = activeSection.defaultData;
                setSectionData(JSON.parse(JSON.stringify(defaults)));
                form.setFieldsValue({ [activeSection.meta.key]: defaults });
              }}
            >
              Reset to Defaults
            </Button>
          ) : undefined,
          actions: [
            { label: "Cancel", onClick: closeDrawer },
            {
              label: "Save Changes",
              type: "primary",
              htmlType: "submit",
              loading: createOrUpdateWebInfoMutation.isPending,
              onClick: handleSave,
            },
          ],
        })}
      >
        <Form
          form={form}
          layout="vertical"
          size="middle"
          key={editingSection}
          initialValues={{ [editingSection || ""]: sectionData }}
          onFinish={handleSave}
          className="!px-0"
        >
          {activeSection && <activeSection.FormFields />}
        </Form>
      </Drawer>
    </div>
  );
};
